import { h } from "preact";
import { useState, useRef, useEffect } from "preact/hooks";
/**
 * Попап заказа.
 *
 * Пропсы:
 *   orderData — { description } — текст заказа
 *   onclose   — колбэк () => void
 */
export function OrderPopup({ orderData = { description: "" }, onclose }) {
  const [stage, setStage] = useState("form");     // "form" | "loader" | "success" | "fail"
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [mail, setMail] = useState("");
  const [text, setText] = useState("");
  const [agree, setAgree] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [phoneError, setPhoneError] = useState(false);
  const [fileRef, setFileRef] = useState(null);
  const phoneRef = useRef(null);

  useEffect(() => {
    if (!phoneRef.current) return;
    if (typeof window.IMask !== "function") {
      console.warn("IMask не загружен — маска телефона отключена");
      return;
    }

    const mask = window.IMask(phoneRef.current, {
      mask: "+{7} (000) 000-00-00",
    });

    return () => mask.destroy();   // очистка при размонтировании
  }, []);


  function validate() {
    const nameOk = name && name.length > 0;
    const phoneOk = phone && phone.length > 0;
    setNameError(!nameOk);
    setPhoneError(!phoneOk);
    return nameOk && phoneOk;
  }

  async function sendOrder() {
    const formData = new FormData();
    formData.append("name", name);
    formData.append("phone", phone);
    formData.append("order", orderData.description);

    if (fileRef?.files?.length > 0) {
      formData.append("attach", fileRef.files[0]);
    }
    if (mail && mail.length > 0) formData.append("mail", mail);
    if (text && text.length > 0) formData.append("text", text);

    setStage("loader");

    try {
      const res = await fetch("/order.php", {
        method: "POST",
        headers: { Accept: "application/json" },
        body: formData,
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${res.status}`);
      }

      const data = await res.json();
      if (!data.ok) throw new Error(data.error || "Order failed");

      setStage("success");

      if (typeof window.sendMetrikaEvent === "function") {
        window.sendMetrikaEvent("allgoals", { type: "form" });
      }
    } catch (e) {
      setStage("fail");
      console.error(e);
    }
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (validate()) sendOrder();
  }

  function close() {
    // сброс не нужен: компонент размонтируется через onclose
    onclose();
  }

  // ─── Оверлей ──────────────────────────────────────
  const overlay = h("div", {
    class: "order-popup__overlay",
    onClick: close,
    role: "presentation",
  });

  // ─── Стадия: form ─────────────────────────────────
  const formStage = h("div", null,
    h("h2", { class: "order-popup__title" }, "Оставьте ваши контакты"),
    h("p", { class: "order-popup__subtitle" }, "Менеджер свяжется с вами в течение 10 минут"),

    h("form", { onSubmit: handleSubmit },
      h("div", { class: "order-popup__field" },
        h("label", { class: "order-popup__label" }, "Имя"),
        h("input", {
          type: "text",
          value: name,
          placeholder: "Как к вам обращаться",
          class: "order-popup__input" + (nameError ? " is--error" : ""),
          onInput: (e) => { setName(e.target.value); setNameError(false); },
        }),
        nameError && h("p", { class: "order-popup__error" }, "Укажите имя")
      ),

      h("div", { class: "order-popup__field" },
        h("label", { class: "order-popup__label" }, "Телефон"),
        h("input", {
          ref: phoneRef,
          type: "tel",
          value: phone,
          placeholder: "+7 (999) 999-99-99",
          class: "order-popup__input" + (phoneError ? " is--error" : ""),
          onInput: (e) => { setPhone(e.target.value); setPhoneError(false); },
        }),
        phoneError && h("p", { class: "order-popup__error" }, "Введите телефон")
      ),

      // «Дополнительно» — свёрнуто, чтобы форма по умолчанию была короткой
      h("details", { class: "order-popup__more" },
        h("summary", { class: "order-popup__more-summary" }, "+ Дополнительно"),

        h("div", { class: "order-popup__more-body" },
          h("div", { class: "order-popup__field" },
            h("label", { class: "order-popup__label" }, "Email"),
            h("input", {
              type: "text",
              value: mail,
              placeholder: "email@example.com",
              class: "order-popup__input",
              onInput: (e) => setMail(e.target.value),
            })
          ),

          h("div", { class: "order-popup__field" },
            h("label", { class: "order-popup__label" }, "Комментарий"),
            h("textarea", {
              value: text,
              rows: 3,
              placeholder: "Что нужно напечатать, сроки",
              class: "order-popup__textarea",
              onInput: (e) => setText(e.target.value),
            })
          ),

          h("div", { class: "order-popup__field" },
            h("label", { class: "order-popup__label" }, "Файл логотипа"),
            h("input", {
              type: "file",
              ref: setFileRef,
              class: "order-popup__file",
            })
          )
        )
      ),

      // Описание заказа
      h("div", { class: "order-popup__order" }, orderData.description),

      // Согласие
      h("label", { class: "order-popup__agree" },
        h("input", {
          type: "checkbox",
          checked: agree,
          required: true,
          onChange: (e) => setAgree(e.target.checked),
          class: "order-popup__agree-checkbox",
        }),
        h("span", null,
          "Отправляя данные, вы даёте согласие на их обработку в соответствии с ",
          h("a", { href: "/privacy.html", target: "_blank" }, "Политикой конфиденциальности")
        )
      ),

      h("button", {
        type: "submit",
        class: "order-popup__submit",
      }, "Отправить")
    )
  );

  // ─── Стадия: loader ───────────────────────────────
  const loaderStage = h("div", { class: "order-popup__center" },
    h("h2", { class: "order-popup__title" }, "Отправляем заявку..."),
    h("div", { class: "order-popup__spinner" })
  );

  // ─── Стадия: success ──────────────────────────────
  const successStage = h("div", { class: "order-popup__center" },
    h("div", { class: "order-popup__icon order-popup__icon--success" },
      h("svg", { xmlns: "http://www.w3.org/2000/svg", fill: "none", viewBox: "0 0 24 24", "stroke-width": "2.5", stroke: "currentColor" },
        h("path", { "stroke-linecap": "round", "stroke-linejoin": "round", d: "m4.5 12.75 6 6 9-13.5" })
      )
    ),
    h("h2", { class: "order-popup__title" }, "Заявка принята"),
    h("p", { class: "order-popup__subtitle" }, "Мы скоро с вами свяжемся"),
    h("button", {
      type: "button",
      class: "order-popup__submit",
      onClick: close,
    }, "Закрыть")
  );

  // ─── Стадия: fail ─────────────────────────────────
  const failStage = h("div", { class: "order-popup__center" },
    h("div", { class: "order-popup__icon order-popup__icon--fail" },
      h("svg", { xmlns: "http://www.w3.org/2000/svg", fill: "none", viewBox: "0 0 24 24", "stroke-width": "2", stroke: "currentColor" },
        h("path", { "stroke-linecap": "round", "stroke-linejoin": "round", d: "M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" })
      )
    ),
    h("h2", { class: "order-popup__title" }, "Что-то пошло не так"),
    h("p", { class: "order-popup__subtitle" }, "Попробуйте позже или позвоните:"),
    h("a", { href: "tel:+79254617129", class: "order-popup__phone" }, "+7 (925) 461-71-29"),
    h("button", {
      type: "button",
      class: "order-popup__submit",
      onClick: close,
    }, "Закрыть")
  );

  // ─── Сборка ───────────────────────────────────────
  return h("div", { class: "order-popup" },
    overlay,

    h("div", {
      class: "order-popup__window",
      role: "dialog",
      tabindex: "-1",
    },
      h("div", {
        class: "order-popup__card",
        onClick: (e) => e.stopPropagation(),
      },
        h("button", {
          type: "button",
          class: "order-popup__close",
          onClick: close,
          "aria-label": "Закрыть",
        },
          h("svg", { xmlns: "http://www.w3.org/2000/svg", fill: "none", viewBox: "0 0 24 24", "stroke-width": "1.8", stroke: "currentColor" },
            h("path", { "stroke-linecap": "round", "stroke-linejoin": "round", d: "M6 18L18 6M6 6l12 12" })
          )
        ),

        stage === "form" && formStage,
        stage === "loader" && loaderStage,
        stage === "success" && successStage,
        stage === "fail" && failStage
      )
    )
  );
}
